import { Component, ElementRef, EventEmitter, HostListener, Input, Output, inject, signal } from '@angular/core';

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  danger?: boolean;
  divider?: boolean;
}

/**
 * Context menu that opens on trigger click and closes on item selection or outside click.
 */
@Component({
  selector: 'es-dropdown-menu',
  template: `
    <div class="relative inline-block">
      <div (click)="toggleOpen()">
        <ng-content select="[trigger]"></ng-content>
      </div>

      @if (isOpen()) {
        <div
          class="absolute right-0 mt-1 min-w-[180px] rounded-lg bg-[var(--bg-card)] border border-[var(--border)] shadow-lg z-50 py-1 animate-scale-in"
        >
          @for (item of items; track item.id) {
            @if (item.divider) {
              <div class="border-t border-[var(--border)] my-1"></div>
            } @else {
              <button
                class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer"
                [class]="item.danger
                  ? 'text-red-500 hover:bg-red-500/10'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)]'"
                (click)="selectItem(item)"
              >
                @if (item.icon) {
                  <span>{{ item.icon }}</span>
                }
                {{ item.label }}
              </button>
            }
          }
        </div>
      }
    </div>
  `,
})
export class DropdownMenuComponent {
  @Input() items: MenuItem[] = [];
  @Output() itemSelected = new EventEmitter<MenuItem>();

  isOpen = signal(false);

  private readonly host = inject(ElementRef<HTMLElement>);

  toggleOpen() {
    this.isOpen.update(v => !v);
  }

  selectItem(item: MenuItem) {
    this.itemSelected.emit(item);
    this.isOpen.set(false);
  }

  /**
   * Close on any click that lands outside this component. The contains-check
   * is essential — without it, the trigger's own click bubbles to document
   * and immediately re-closes the menu we just opened. `event` is optional
   * so the method stays callable from tests without faking a DOM event.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: Event) {
    const target = event?.target as Node | null | undefined;
    if (target && this.host.nativeElement.contains(target)) return;
    this.isOpen.set(false);
  }
}
